import { Suspense, lazy, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from '@seller/shared';
import {
  api,
  fetchLeadTimeModels,
  type GridCell,
  type GridProduct,
  type ModelLeadTime,
  type OfferSummaryRow,
  type OffersSummaryResult,
  type ProductNode,
  type ProductNodesResult,
  type ProductRow,
  type ProductsResult,
  type PutUpMixEntry,
  type SizeGridResult,
  type TaxonomySummary,
} from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { OffersCard } from '../components/OffersCard.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { Sparkline, useUnitSparklines } from '../components/Sparkline.js';
import { WhenVisible } from '../components/WhenVisible.js';
import {
  Alert,
  Button,
  Card,
  PageHeader,
  Spinner,
  StockNote,
  TableScroll,
} from '../components/ui.js';
import { day, decimal, integer } from '../format.js';
import {
  COVER_BAND_LABEL,
  COVER_TINT,
  STOCK_STATE_LABEL,
  categoryLabel,
  gaugeLabel,
  productPagePath,
  productsPath,
  putUpLabel,
  putUpWithLength,
} from '../lib/products.js';
import { FamiliesPage } from './FamiliesPage.js';

const EmbeddedSalesStock = lazy(() => import('../charts/EmbeddedSalesStock.js'));
const ProductScatter = lazy(() => import('../charts/ProductScatter.js'));

/**
 * The Products browser: category → series → model → size grid → product. The
 * product identity is the child ASIN — every seller SKU on it is one product,
 * metrics summed across them. The drill path lives in the URL query string
 * (`?category=&series=&model=&asin=`), so every view is linkable and the back
 * button works. A "Table" toggle shows the existing Families table (its old
 * `/families` route redirects here). See docs/products.md.
 */

// The order the category cards appear in (Unassigned last), independent of size.
const CATEGORY_CARD_ORDER = [
  'racquet',
  'string',
  'grip',
  'footwear',
  'bag',
  'other',
  '__unassigned__',
];

export function ProductsPage() {
  const [params] = useSearchParams();
  const view = params.get('view');
  const category = params.get('category') ?? undefined;
  const series = params.get('series') ?? undefined;
  const model = params.get('model') ?? undefined;
  const asin = params.get('asin') ?? undefined;

  const drill = { category, series, model };

  if (view === 'table') {
    return (
      <div className="space-y-4">
        <ProductsHeader tab="table" drill={drill} />
        <FamiliesPage heading={false} />
      </div>
    );
  }

  if (view === 'scatter') {
    return (
      <div className="space-y-4">
        <ProductsHeader tab="scatter" drill={drill} />
        <Breadcrumb category={category} series={series} model={model} asin={undefined} />
        <AnalysisBanner />
        <ScatterView category={category} series={series} model={model} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <ProductsHeader tab="browse" drill={drill} />
      <Breadcrumb category={category} series={series} model={model} asin={asin} />
      <AnalysisBanner />
      {asin !== undefined && category && series && model ? (
        <ProductView category={category} series={series} model={model} asin={asin} />
      ) : model !== undefined && category && series ? (
        <ModelGrid category={category} series={series} model={model} />
      ) : series !== undefined && category ? (
        <ModelsList category={category} series={series} />
      ) : category !== undefined ? (
        <SeriesList category={category} />
      ) : (
        <CategoryCards />
      )}
    </div>
  );
}

/** The page header with the Browse / Scatter / Table toggle. */
function ProductsHeader({
  tab,
  drill,
}: {
  tab: 'browse' | 'scatter' | 'table';
  drill?: {
    category?: string | undefined;
    series?: string | undefined;
    model?: string | undefined;
  };
}) {
  // The Scatter tab keeps the current drill, so toggling to it stays in scope.
  const scatterTo = productsPath({
    view: 'scatter',
    category: drill?.category,
    series: drill?.series,
    model: drill?.model,
  });
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <PageHeader
        title="Products"
        description="Browse the catalog the way a shopper thinks about it — by category, series, model, and size — over the taxonomy the app derives per product (the child ASIN). Every seller SKU on one ASIN is one product, its figures summed."
      />
      <div className="flex items-center gap-1 rounded-md ring-1 ring-inset ring-line p-0.5">
        <TabLink to={productsPath({})} active={tab === 'browse'} label="Browse" />
        <TabLink to={scatterTo} active={tab === 'scatter'} label="Scatter" />
        <TabLink to={productsPath({ view: 'table' })} active={tab === 'table'} label="Table" />
      </div>
    </div>
  );
}

/** The Scatter view, scoped to the current drill (account/category/series/model). */
function ScatterView({
  category,
  series,
  model,
}: {
  category?: string | undefined;
  series?: string | undefined;
  model?: string | undefined;
}) {
  const filter = { category, series, model };
  const scopeLabel =
    model !== undefined
      ? model === '__no_model__'
        ? '(no model)'
        : model
      : series !== undefined
        ? series === '__no_series__'
          ? '(no series)'
          : series
        : category !== undefined
          ? categoryLabel(category)
          : 'the whole account';
  // Color by category at the top (no category picked yet), else by series.
  const defaultColorBy = category === undefined ? 'category' : 'series';
  return (
    <Card className="p-4">
      <Suspense fallback={<Spinner label="Loading scatter" />}>
        <ProductScatter
          filter={filter}
          scopeLabel={scopeLabel}
          defaultColorBy={defaultColorBy}
          urlState
        />
      </Suspense>
    </Card>
  );
}

/** A link that opens the Scatter view scoped to the given drill. */
function ScatterLink({
  category,
  series,
  model,
}: {
  category?: string | undefined;
  series?: string | undefined;
  model?: string | undefined;
}) {
  return (
    <Link
      to={productsPath({ view: 'scatter', category, series, model })}
      data-testid="scatter-link"
      className="inline-flex items-center rounded-md bg-surface px-3 py-1.5 text-sm font-medium text-body ring-1 ring-inset ring-line-strong hover:bg-sunken"
    >
      Scatter
    </Link>
  );
}

function TabLink({ to, active, label }: { to: string; active: boolean; label: string }) {
  return (
    <Link
      to={to}
      className={`rounded px-3 py-1.5 text-sm font-medium ${
        active ? 'bg-raised text-strong' : 'text-muted hover:bg-sunken hover:text-body'
      }`}
    >
      {label}
    </Link>
  );
}

/** The drill breadcrumb; each crumb links back to the level above. */
function Breadcrumb({
  category,
  series,
  model,
  asin,
}: {
  category?: string | undefined;
  series?: string | undefined;
  model?: string | undefined;
  asin?: string | undefined;
}) {
  const crumbs: { label: string; to: string | null }[] = [
    { label: 'Products', to: productsPath({}) },
  ];
  if (category) {
    crumbs.push({
      label: categoryLabel(category),
      to: series ? productsPath({ category }) : null,
    });
  }
  if (series && category) {
    crumbs.push({
      label: series === '__no_series__' ? '(no series)' : series,
      to: model ? productsPath({ category, series }) : null,
    });
  }
  if (model && category && series) {
    crumbs.push({
      label: model === '__no_model__' ? '(no model)' : model,
      to: asin ? productsPath({ category, series, model }) : null,
    });
  }
  return (
    <nav className="text-xs text-muted" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <span key={c.label + i}>
          {i > 0 && ' / '}
          {c.to ? (
            <Link to={c.to} className="hover:underline">
              {c.label}
            </Link>
          ) : (
            <span className="text-body">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Category cards
// ---------------------------------------------------------------------------

function CategoryCards() {
  const query = useQuery({
    queryKey: ['products', 'category'],
    queryFn: () => api.get<ProductNodesResult>('/api/products?level=category'),
  });
  // Listing issues get their own link, so they never hide among series-naming work.
  const summary = useQuery({
    queryKey: ['taxonomy-summary'],
    queryFn: () => api.get<TaxonomySummary>('/api/taxonomy/summary'),
  });
  const listingIssues = summary.data?.listingIssues ?? 0;

  if (query.isPending) return <Spinner label="Loading products" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;

  const byKey = new Map(query.data.nodes.map((n) => [n.categoryKey, n]));
  const ordered = CATEGORY_CARD_ORDER.map((k) => byKey.get(k)).filter(
    (n): n is ProductNode => n !== undefined,
  );

  if (ordered.length === 0) return <EmptyState />;

  // The live queue (products with an Active SKU); the summary's count once it has
  // loaded, else the cards' own badges, which count the same live queue.
  const reviewNeeded =
    summary.data?.needsReview ?? ordered.reduce((sum, n) => sum + n.reviewNeeded, 0);
  const retiredHidden = summary.data?.retiredNeedsReview ?? 0;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {reviewNeeded > 0 ? (
            <Link
              to="/products/review"
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
            >
              Review {integer(reviewNeeded)} to check
            </Link>
          ) : (
            <Link to="/products/review" className="text-sm text-brand-600 hover:underline">
              Review the taxonomy
            </Link>
          )}
          {retiredHidden > 0 && (
            <span
              className="text-sm text-muted"
              title="Products none of whose SKUs is Active; the review page lists them with “Include retired products”"
              data-testid="retired-hidden"
            >
              {integer(retiredHidden)} retired hidden
            </span>
          )}
          {listingIssues > 0 && (
            <Link
              to="/products/review?filter=listing-issues"
              className="inline-flex items-center rounded-md bg-warn-bg px-3 py-2 text-sm font-medium text-warn-text ring-1 ring-inset ring-warn-line hover:underline"
              title="Listings whose Amazon product type disagrees with their category, or is one the app does not map"
              data-testid="listing-issues-link"
            >
              {integer(listingIssues)} listing issue{listingIssues === 1 ? '' : 's'}
            </Link>
          )}
        </div>
        <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((n) => (
          <Link
            key={n.categoryKey}
            to={productsPath({ category: n.categoryKey })}
            data-testid="category-card"
            className="block rounded-lg bg-surface p-5 ring-1 ring-inset ring-line hover:ring-line-strong"
          >
            <div className="flex items-baseline justify-between">
              <h2 className="text-base font-semibold text-strong">{n.label}</h2>
              {n.reviewNeeded > 0 && (
                <span
                  className="rounded bg-warn-bg px-1.5 py-0.5 text-xs font-medium text-warn-text ring-1 ring-inset ring-warn-line"
                  title={`${n.reviewNeeded} product(s) still need a taxonomy decision`}
                >
                  {integer(n.reviewNeeded)} to review
                </span>
              )}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-sm">
              <Figure label="Products" value={integer(n.productCount)} />
              <Figure label="SKUs" value={integer(n.skuCount)} />
              <Figure label="Units 365" value={integer(n.units365)} />
              <Figure label="On hand" value={integer(n.onHand)} />
            </dl>
          </Link>
        ))}
      </div>
    </>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between pr-4">
      <dt className="text-muted">{label}</dt>
      <dd className="tabular font-medium text-strong">{value}</dd>
    </div>
  );
}

/** Shown when there is no taxonomy yet: explain, and link to the review/recompute. */
function EmptyState() {
  return (
    <Card className="p-6">
      <h2 className="text-sm font-medium text-strong">Nothing categorized yet</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted">
        The Products area needs the taxonomy the app derives from your listing titles and matched
        supplier bookings. Import an All Listings report, then upload and match a booking, and the
        categories fill in. You can also review and recompute the taxonomy by hand.
      </p>
      <div className="mt-4 flex items-center gap-3">
        <Link
          to="/products/review"
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-brand-on hover:bg-brand-700"
        >
          Open the review page
        </Link>
        <RecomputeButton />
      </div>
    </Card>
  );
}

/** A recompute button for importers; a viewer never sees it (the API refuses too). */
function RecomputeButton() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canEdit = session.status === 'signedIn' && can(session.user.role, 'data:import');
  const recompute = useMutation({
    mutationFn: () => api.post<{ count: number }>('/api/taxonomy/recompute'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['taxonomy-summary'] });
    },
  });
  if (!canEdit) return null;
  return (
    <Button variant="secondary" disabled={recompute.isPending} onClick={() => recompute.mutate()}>
      {recompute.isPending ? 'Recomputing…' : 'Recompute taxonomy'}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Series list (rows with a relative sales bar)
// ---------------------------------------------------------------------------

function SeriesList({ category }: { category: string }) {
  const query = useQuery({
    queryKey: ['products', 'series', category],
    queryFn: () =>
      api.get<ProductNodesResult>(
        `/api/products?level=series&category=${encodeURIComponent(category)}`,
      ),
  });
  // Every row's twelve-month units line, in one batched request for the page.
  const seriesKeys = useMemo(
    () => (query.data?.nodes ?? []).flatMap((n) => (n.seriesKey === null ? [] : [n.seriesKey])),
    [query.data],
  );
  const parent = useMemo(() => ({ category }), [category]);
  const sparklines = useUnitSparklines('series', parent, seriesKeys);

  if (query.isPending) return <Spinner label="Loading series" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;
  if (query.data.nodes.length === 0)
    return <Card className="p-6 text-sm text-muted">No series in this category.</Card>;

  return (
    <Card>
      <div className="flex items-center justify-between border-b border-line px-4 py-2">
        <span className="text-sm font-medium text-body">
          {integer(query.data.nodes.length)} series
        </span>
        <ScatterLink category={category} />
      </div>
      <ul className="divide-y divide-line">
        {query.data.nodes.map((n) => (
          <li key={n.seriesKey}>
            <Link
              to={productsPath({ category, series: n.seriesKey ?? undefined })}
              data-testid="series-row"
              className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 hover:bg-sunken"
            >
              <div className="min-w-40 flex-1">
                <div className="font-medium text-strong">
                  {n.series ?? '(no series)'}
                  {n.reviewNeeded > 0 && (
                    <span
                      className="ml-2 rounded bg-warn-bg px-1.5 py-0.5 text-xs font-medium text-warn-text ring-1 ring-inset ring-warn-line"
                      title={`${n.reviewNeeded} product(s) need a taxonomy decision`}
                    >
                      {integer(n.reviewNeeded)} to review
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted">
                  {integer(n.childCount)} model{n.childCount === 1 ? '' : 's'} ·{' '}
                  {integer(n.productCount)} product{n.productCount === 1 ? '' : 's'}
                </div>
              </div>
              <div
                className="w-28"
                title={`${integer(n.units365)} units in the last 365 days. The line is units per month over the last 12 months, scaled to this series' own busiest month, so its height shows shape, not size.`}
              >
                <Sparkline
                  series={n.seriesKey === null ? undefined : sparklines.byKey.get(n.seriesKey)}
                  pending={sparklines.isPending}
                  label={n.series ?? '(no series)'}
                />
                <div className="text-right text-xs text-muted">{integer(n.units365)} U365</div>
              </div>
              <NodeFigures node={n} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** The compact figure block reused by the series rows. */
function NodeFigures({ node }: { node: ProductNode }) {
  return (
    <div className="flex gap-4 text-xs text-muted">
      <span title="On hand">
        Qty <span className="tabular font-medium text-body">{integer(node.onHand)}</span>
      </span>
      <span title="Units per 30 in-stock days (in-stock-basis children)">
        U/30{' '}
        <span className="tabular font-medium text-body">
          {decimal(node.unitsPer30InStock ?? null, 1)}
        </span>
      </span>
      <span title="In-stock SKUs of the total">
        In stk <span className="tabular font-medium text-body">{integer(node.inStockCount)}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Models table
// ---------------------------------------------------------------------------

function ModelsList({ category, series }: { category: string; series: string }) {
  const navigate = useNavigate();
  const [showCompetition, setShowCompetition] = useState(false);
  const query = useQuery({
    queryKey: ['products', 'model', category, series],
    queryFn: () =>
      api.get<ProductNodesResult>(
        `/api/products?level=model&category=${encodeURIComponent(category)}&series=${encodeURIComponent(series)}`,
      ),
  });
  // The competitor-offers roll-up is fetched only when the toggle is on, so the
  // default table costs nothing extra. Keyed by ASIN for the per-model roll-up.
  const summary = useQuery({
    queryKey: ['offers-summary'],
    queryFn: () => api.get<OffersSummaryResult>('/api/offers/summary'),
    enabled: showCompetition,
  });
  const byAsin = useMemo(() => {
    const map = new Map<string, OfferSummaryRow>();
    for (const row of summary.data?.rows ?? []) map.set(row.asin, row);
    return map;
  }, [summary.data]);

  // Observed lead times per model, shown at the model level when n >= 3. Keyed by
  // the model name within this category+series view. See docs/lead-times.md.
  const leadTimes = useQuery({
    queryKey: ['lead-times', 'models'],
    queryFn: () => fetchLeadTimeModels(),
  });
  const leadByModel = useMemo(() => {
    // The URL carries null category/series as the `__no_*__` sentinels, while the
    // lead-time rows carry a real null; normalize both sides so a null-series model
    // still shows its Lead p80 rather than silently never matching.
    const normCategory = category === '__no_category__' ? null : (category ?? null);
    const normSeries = series === '__no_series__' ? null : (series ?? null);
    const map = new Map<string, ModelLeadTime>();
    for (const row of leadTimes.data?.models ?? []) {
      if (row.category === normCategory && row.series === normSeries && row.model !== null) {
        map.set(row.model, row);
      }
    }
    return map;
  }, [leadTimes.data, category, series]);

  // Every model's twelve-month units line, in one batched request for the table.
  const modelKeys = useMemo(
    () => (query.data?.nodes ?? []).flatMap((n) => (n.modelKey === null ? [] : [n.modelKey])),
    [query.data],
  );
  const sparkParent = useMemo(() => ({ category, series }), [category, series]);
  const sparklines = useUnitSparklines('model', sparkParent, modelKeys);

  if (query.isPending) return <Spinner label="Loading models" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;
  if (query.data.nodes.length === 0)
    return <Card className="p-6 text-sm text-muted">No models in this series.</Card>;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
        <span className="text-sm font-medium text-body">
          {integer(query.data.nodes.length)} models
        </span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowCompetition((v) => !v)}
            className="rounded border border-line px-2 py-0.5 text-xs text-strong hover:bg-sunken"
          >
            {showCompetition ? 'Hide competition' : 'Show competition'}
          </button>
          <ScatterLink category={category} series={series} />
          <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
        </div>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Model</th>
              <th className="px-3 py-2 text-right font-medium">Products</th>
              <th className="px-3 py-2 text-right font-medium">SKUs</th>
              <th className="px-3 py-2 text-right font-medium">On hand</th>
              <th className="px-3 py-2 text-right font-medium">U30</th>
              <th className="px-3 py-2 text-right font-medium">U365</th>
              <th
                className="px-3 py-2 font-medium"
                title="Units ordered per calendar month, the last 12 months; the current month is to date (dashed). Each line is scaled to its own busiest month, so heights show shape, not size; U365 carries the size."
              >
                12 months
              </th>
              <th className="px-3 py-2 text-right font-medium">U/30 IS</th>
              <th className="px-3 py-2 text-right font-medium">OOS 365</th>
              <th
                className="px-3 py-2 text-right font-medium"
                title="Observed lead time (p80 of total days), shown when at least 3 sent orders have been matched"
              >
                Lead p80
              </th>
              <th className="px-3 py-2 font-medium">Last sale</th>
              {showCompetition && (
                <>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title="Most sellers on any one listing"
                  >
                    Sellers
                  </th>
                  <th
                    className="px-3 py-2 font-medium"
                    title="Products where we hold the Buy Box / a competitor holds it"
                  >
                    Buy Box
                  </th>
                  <th
                    className="px-3 py-2 text-right font-medium"
                    title="Products where a competitor's lowest FBA price is below ours"
                  >
                    Undercut
                  </th>
                  <th
                    className="px-3 py-2 font-medium"
                    title="Products with a competitor gone from the latest poll (likely stockout) / a new competitor, in the last two weeks"
                  >
                    Signals
                  </th>
                </>
              )}
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {query.data.nodes.map((n) => (
              <tr
                key={n.modelKey}
                data-testid="model-row"
                className="cursor-pointer hover:bg-sunken"
                onClick={() =>
                  void navigate(productsPath({ category, series, model: n.modelKey ?? undefined }))
                }
              >
                <td className="px-3 py-1.5 font-medium text-strong">
                  <span className="inline-flex items-center gap-2 align-middle">
                    <ProductThumb asin={n.asins[0]} title={n.label} />
                    <Link
                      to={productsPath({ category, series, model: n.modelKey ?? undefined })}
                      className="text-brand-600 hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {n.label}
                    </Link>
                  </span>
                  {n.reviewNeeded > 0 && (
                    <span
                      className="ml-2 rounded bg-warn-bg px-1.5 py-0.5 text-[10px] font-medium text-warn-text ring-1 ring-inset ring-warn-line"
                      title={`${n.reviewNeeded} product(s) need a taxonomy decision`}
                    >
                      {integer(n.reviewNeeded)}
                    </span>
                  )}
                </td>
                <td className="tabular px-3 py-1.5 text-right">{integer(n.productCount)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(n.skuCount)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(n.onHand)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(n.units30)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(n.units365)}</td>
                <td className="px-3 py-1.5">
                  <Sparkline
                    series={n.modelKey === null ? undefined : sparklines.byKey.get(n.modelKey)}
                    pending={sparklines.isPending}
                    label={n.label}
                  />
                </td>
                <td className="tabular px-3 py-1.5 text-right">
                  {decimal(n.unitsPer30InStock ?? null, 1)}
                </td>
                <td className="tabular px-3 py-1.5 text-right">
                  <span title={`Mean ${decimal(n.stockoutDays365Mean, 1)} across products`}>
                    {integer(n.stockoutDays365Max)}
                  </span>
                </td>
                <td className="tabular px-3 py-1.5 text-right">
                  <ModelLeadCell lead={n.model !== null ? leadByModel.get(n.model) : undefined} />
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">{day(n.lastSaleDate)}</td>
                {showCompetition && <CompetitionCells node={n} byAsin={byAsin} />}
                <td className="px-3 py-1.5 text-right text-brand-600">→</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}

/**
 * The observed lead time (p80 of total days) for a model, shown only once at least
 * three sent orders have been matched — below that the number is too thin to trust.
 * See docs/lead-times.md.
 */
function ModelLeadCell({ lead }: { lead: ModelLeadTime | undefined }) {
  if (!lead || lead.n < 3 || lead.p80Total === null) {
    return <span className="text-muted">—</span>;
  }
  return (
    <span title={`Median ${lead.medianTotal ?? '—'}d over ${lead.n} matched orders`}>
      {integer(lead.p80Total)}d
    </span>
  );
}

/**
 * The three competition cells for a model row, rolled up over its products' latest
 * offer snapshots (the offers summary, joined by ASIN): the most sellers on any one
 * of its listings, how many products we hold the Buy Box on vs a competitor, and how
 * many are undercut on FBA. An em dash when none of the model's ASINs have been
 * polled yet.
 */
function CompetitionCells({
  node,
  byAsin,
}: {
  node: ProductNode;
  byAsin: Map<string, OfferSummaryRow>;
}) {
  const rows = node.asins.map((a) => byAsin.get(a)).filter((r): r is OfferSummaryRow => !!r);
  if (rows.length === 0) {
    return (
      <>
        <td className="px-3 py-1.5 text-right text-muted">—</td>
        <td className="px-3 py-1.5 text-muted">—</td>
        <td className="px-3 py-1.5 text-right text-muted">—</td>
        <td className="px-3 py-1.5 text-muted">—</td>
      </>
    );
  }
  const sellersMax = Math.max(...rows.map((r) => r.sellerCount));
  const buyBoxUs = rows.filter((r) => r.buyBoxHolder === 'us').length;
  const buyBoxOther = rows.filter((r) => r.buyBoxHolder === 'other').length;
  const undercut = rows.filter((r) => r.undercut).length;
  const disappeared = rows.filter((r) => r.competitorDisappeared).length;
  const newComp = rows.filter((r) => r.newCompetitor).length;
  return (
    <>
      <td className="tabular px-3 py-1.5 text-right" title={`${rows.length} listing(s) tracked`}>
        {integer(sellersMax)}
      </td>
      <td className="tabular px-3 py-1.5" title="We hold / a competitor holds the Buy Box">
        <span className={buyBoxUs > 0 ? 'text-ok-text' : ''}>{integer(buyBoxUs)}</span>
        <span className="text-muted"> / </span>
        <span className={buyBoxOther > 0 ? 'text-warn-text' : ''}>{integer(buyBoxOther)}</span>
      </td>
      <td className="tabular px-3 py-1.5 text-right">
        <span className={undercut > 0 ? 'text-warn-text' : 'text-muted'}>{integer(undercut)}</span>
      </td>
      <td className="px-3 py-1.5 text-xs">
        {disappeared === 0 && newComp === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {disappeared > 0 && (
              <span
                className="rounded bg-warn-bg px-1.5 py-0.5 font-medium text-warn-text"
                title={`${disappeared} product(s) with a competitor gone from the latest poll`}
              >
                {integer(disappeared)} gone
              </span>
            )}
            {newComp > 0 && (
              <span
                className="rounded px-1.5 py-0.5 font-medium text-body ring-1 ring-inset ring-line"
                title={`${newComp} product(s) with a new competitor in the last two weeks`}
              >
                {integer(newComp)} new
              </span>
            )}
          </span>
        )}
      </td>
    </>
  );
}

// ---------------------------------------------------------------------------
// The size grid (a model)
// ---------------------------------------------------------------------------

function ModelGrid({
  category,
  series,
  model,
}: {
  category: string;
  series: string;
  model: string;
}) {
  const query = useQuery({
    queryKey: ['products', 'grid', category, series, model],
    queryFn: () =>
      api.get<SizeGridResult>(
        `/api/products/grid?category=${encodeURIComponent(category)}&series=${encodeURIComponent(series)}&model=${encodeURIComponent(model)}`,
      ),
  });

  if (query.isPending) return <Spinner label="Loading size grid" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;
  const grid = query.data;

  // A representative image for the model: the first carried product's ASIN.
  const modelAsin =
    grid.rows
      .flatMap((row) => row.cells)
      .flatMap((cell) => (cell.kind === 'carried' ? cell.products : []))
      .map((p) => p.asin)
      .find((a): a is string => !!a) ?? null;

  // A string lays out by gauge (columns) and put-up (rows, then color within it);
  // every other category keeps its color rows and size columns unchanged.
  const isString = grid.category === 'string';

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-body">
          <ProductThumb asin={modelAsin} title={grid.model} />
          Size grid
          {isString && <span className="text-xs font-normal text-muted">by gauge and put-up</span>}
        </span>
        <div className="flex items-center gap-3">
          <ScatterLink category={category} series={series} model={model} />
          <StockNote source={grid.stock.source} asOf={grid.stock.asOf} />
        </div>
      </div>
      {grid.putUpMix && grid.putUpMix.length > 0 && <PutUpMix mix={grid.putUpMix} />}
      <TableScroll>
        <table className="w-full border-collapse text-sm" data-testid="size-grid">
          <thead>
            <tr className="text-xs text-muted">
              <th className="border-b border-line px-3 py-2 text-left font-medium">
                {isString ? 'Put-up / gauge' : 'Color'}
              </th>
              {grid.columns.map((c) => (
                <th
                  key={c.key}
                  className="border-b border-line px-2 py-2 text-center font-medium"
                  data-testid="grid-column"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <tr key={`${row.putUp ?? ''}|${row.color ?? '__none__'}`} data-testid="grid-row">
                <td className="whitespace-nowrap border-b border-line px-3 py-2 font-medium text-body">
                  {isString ? (
                    <>
                      <span data-testid="grid-row-put-up">
                        {row.putUp ? (
                          putUpLabel(row.putUp)
                        ) : (
                          <span className="italic text-muted">{putUpLabel(null)}</span>
                        )}
                      </span>
                      {row.color && <span className="font-normal text-muted"> · {row.color}</span>}
                    </>
                  ) : (
                    (row.color ?? <span className="italic text-muted">Color unknown</span>)
                  )}
                </td>
                {grid.columns.map((col) => {
                  const cell = row.cells.find((c) => c.columnKey === col.key);
                  return (
                    <td key={col.key} className="border-b border-line p-1 align-top">
                      <GridCellView cell={cell} category={category} series={series} model={model} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      <GridLegend />
    </Card>
  );
}

/**
 * A string model's put-up mix, under the grid header: each put-up's share of the
 * model's 365-day units, and how many products carry it.
 */
function PutUpMix({ mix }: { mix: PutUpMixEntry[] }) {
  const anyUnits = mix.some((m) => m.units365 > 0);
  return (
    <div
      className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-4 py-2 text-xs text-muted"
      data-testid="put-up-mix"
    >
      <span className="font-medium text-body">Put-up mix</span>
      {mix.map((m) => (
        <span key={m.putUp ?? '__unknown__'} className="tabular">
          <span className="text-body">{m.label}</span> {anyUnits ? `${m.share}%` : '—'} ·{' '}
          {integer(m.products)} {m.products === 1 ? 'product' : 'products'}
        </span>
      ))}
      <span>(share of units, last 365 days)</span>
    </div>
  );
}

function GridCellView({
  cell,
  category,
  series,
  model,
}: {
  cell: GridCell | undefined;
  category: string;
  series: string;
  model: string;
}) {
  if (!cell) return <div className="min-h-12 rounded bg-transparent" aria-hidden />;

  if (cell.kind === 'not_carried') {
    const demo = cell.category === 'demo_racquet';
    return (
      <div
        className="min-h-12 rounded border border-dashed border-line-strong bg-sunken/50 px-2 py-1 text-xs text-muted"
        title={`Offered by the booking, not carried (item ${cell.itemCode}${demo ? ', demo' : ''})`}
      >
        <div className="font-medium">Not carried{demo ? ' · demo' : ''}</div>
        <div className="tabular">
          {cell.unitCost ? `$${decimal(cell.unitCost, 2)}` : '—'}
          {cell.map ? ` · MAP $${decimal(cell.map, 2)}` : ''}
        </div>
      </div>
    );
  }

  // A carried cell holds one product, or a stack when two child ASINs share the
  // color and size — every product stays reachable.
  if (cell.products.length === 1) {
    return (
      <GridProductTile
        product={cell.products[0]!}
        category={category}
        series={series}
        model={model}
      />
    );
  }
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium uppercase tracking-wide text-muted">
        {cell.products.length} products
      </div>
      {cell.products.map((p) => (
        <GridProductTile
          key={p.productKey}
          product={p}
          category={category}
          series={series}
          model={model}
        />
      ))}
    </div>
  );
}

function GridProductTile({
  product,
  category,
  series,
  model,
}: {
  product: GridProduct;
  category: string;
  series: string;
  model: string;
}) {
  const tint = COVER_TINT[product.coverBand];
  // A string tile's tooltip also names its metric diameter and length, when stated.
  const stringDetail = [
    product.gaugeMm ? `${product.gaugeMm} mm` : null,
    product.putUpLength,
  ].filter((v): v is string => !!v);
  return (
    <Link
      to={productsPath({ category, series, model, asin: product.productKey })}
      className={`block min-h-12 rounded px-2 py-1 text-xs ring-1 ring-inset hover:opacity-80 ${tint}`}
      title={[
        STOCK_STATE_LABEL[product.stockState],
        COVER_BAND_LABEL[product.coverBand],
        ...stringDetail,
      ].join(' · ')}
    >
      <div className="tabular font-medium">{integer(product.onHand)} on hand</div>
      <div className="tabular">
        {product.daysOfCover ? `${decimal(product.daysOfCover, 0)}d cover` : 'no cover'} ·{' '}
        {integer(product.units90)} U90
      </div>
    </Link>
  );
}

function GridLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-line px-4 py-2 text-xs text-muted">
      <span>Days of cover on hand:</span>
      {(['critical', 'low', 'healthy', 'ample', 'unknown'] as const).map((band) => (
        <span key={band} className="inline-flex items-center gap-1">
          <span className={`inline-block h-3 w-3 rounded ring-1 ring-inset ${COVER_TINT[band]}`} />
          {COVER_BAND_LABEL[band]}
        </span>
      ))}
      <span className="inline-flex items-center gap-1">
        <span className="inline-block h-3 w-3 rounded border border-dashed border-line-strong bg-sunken" />
        Not carried (from the booking)
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Product view (one child ASIN)
// ---------------------------------------------------------------------------

function ProductView({
  category,
  series,
  model,
  asin,
}: {
  category: string;
  series: string;
  model: string;
  asin: string;
}) {
  const query = useQuery({
    queryKey: ['products', 'product', category, series, model],
    queryFn: () =>
      api.get<ProductsResult>(
        `/api/products?level=product&category=${encodeURIComponent(category)}&series=${encodeURIComponent(series)}&model=${encodeURIComponent(model)}`,
      ),
  });

  const product = useMemo(
    () =>
      query.data?.products.find((p) => (p.asin ?? p.productKey) === asin || p.productKey === asin),
    [query.data, asin],
  );

  if (query.isPending) return <Spinner label="Loading product" />;
  if (query.isError) return <Alert>{query.error.message}</Alert>;
  if (!product) return <Alert>Product not found in this model.</Alert>;

  return <ProductCard product={product} coverage={query.data.coverage} />;
}

function ProductCard({
  product,
  coverage,
}: {
  product: ProductRow;
  coverage: ProductsResult['coverage'];
}) {
  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <ProductThumb asin={product.asin} title={product.name} />
            <h2 className="text-base font-semibold text-strong">{product.name}</h2>
          </div>
          <div className="flex items-center gap-3">
            {product.asin && <span className="font-mono text-xs text-muted">{product.asin}</span>}
            <Link
              to={productPagePath({ asin: product.asin, skuId: product.skuId })}
              className="text-sm font-medium text-brand-600 hover:underline"
            >
              Open product
            </Link>
          </div>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
          <Item label="Color" value={product.color ?? '—'} />
          {product.category === 'string' ? (
            <>
              <Item label="Gauge" value={gaugeLabel(product.gauge, product.gaugeMm)} />
              <Item label="Put-up" value={putUpWithLength(product.putUp, product.putUpLength)} />
            </>
          ) : (
            <Item label="Size" value={product.sizeLabel ?? '—'} />
          )}
          <Item label="On hand" value={integer(product.onHand)} />
          <Item label="Inbound" value={integer(product.inbound)} />
          <Item label="Units 30" value={integer(product.units30)} />
          <Item label="Units 365" value={integer(product.units365)} />
          <Item label="U/30 in-stock" value={decimal(product.unitsPer30InStock ?? null, 2)} />
          <Item label="Last sale" value={day(product.lastSaleDate)} />
        </dl>
        {product.family && (
          <div className="mt-3 border-t border-line pt-2 text-xs text-muted">
            Family:{' '}
            <Link
              to={`/families/detail?key=${encodeURIComponent(product.family.key)}`}
              className="text-brand-600 hover:underline"
            >
              {product.family.name}
            </Link>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <WhenVisible>
          <Suspense fallback={<Spinner label="Loading chart" />}>
            <EmbeddedSalesStock
              scope="family"
              chartKey={product.family?.key ?? product.asin ?? String(product.skuId)}
            />
          </Suspense>
        </WhenVisible>
      </Card>

      {product.asin && <OffersCard asin={product.asin} />}

      <Card>
        <div className="border-b border-line px-4 py-2 text-sm font-medium text-body">
          {integer(product.skus.length)} seller SKU{product.skus.length === 1 ? '' : 's'} on this
          ASIN
        </div>
        <TableScroll>
          <table className="w-full text-sm">
            <thead className="border-b border-line text-left text-xs text-muted">
              <tr>
                <th className="px-3 py-2 font-medium">Seller SKU</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">On hand</th>
                <th className="px-3 py-2 text-right font-medium">Inbound</th>
                <th className="px-3 py-2 text-right font-medium">Reserved</th>
                <th className="px-3 py-2 text-right font-medium">U365</th>
                <th className="px-3 py-2 font-medium">Last sale</th>
                <th className="px-3 py-2 font-medium">On the plan</th>
                <th className="px-3 py-2 font-medium">History</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {product.skus.map((s) => (
                <tr key={s.id}>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <Link to={`/skus/${s.id}`} className="text-brand-600 hover:underline">
                      {s.sellerSku}
                    </Link>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-body">{s.status ?? '—'}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(s.onHand)}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(s.inbound)}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(s.reserved)}</td>
                  <td className="tabular px-3 py-1.5 text-right">{integer(s.units365)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{day(s.lastSaleDate)}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        s.duplicateHidden
                          ? 'bg-warn-bg text-warn-text ring-warn-line'
                          : 'text-muted ring-line'
                      }`}
                    >
                      {s.duplicateHidden ? 'Hidden' : 'Shown'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <HistoryCell sku={s} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
        <div className="border-t border-line px-4 py-2 text-xs text-muted">
          History captured means a stale twin's last sale falls inside the imported order coverage (
          {day(coverage.order.from)}–{day(coverage.order.to)}) and ledger coverage (
          {day(coverage.ledger.from)}–{day(coverage.ledger.to)}), so removing it in Seller Central
          would not lose imported sales.
        </div>
      </Card>
    </div>
  );
}

/** The history-captured reading, shown only for the stale (non-Active) twins. */
function HistoryCell({ sku }: { sku: ProductRow['skus'][number] }) {
  if (sku.status === 'Active') return <span className="text-muted">—</span>;
  return sku.historyCaptured ? (
    <span
      className="text-ok-text"
      title="First sale after the import began and last sale inside both coverages — safe to remove"
    >
      Captured
    </span>
  ) : (
    <span
      className="text-warn-text"
      title={`History may be lost (${sku.historyReason ?? 'check first'})`}
    >
      Check first{sku.historyReason ? ` · ${sku.historyReason}` : ''}
    </span>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="text-body">{value}</dd>
    </div>
  );
}
